import {useCallback, useEffect, useState} from "react";
import { Auth } from "@aws-amplify/auth";
import { useLocation } from "react-router-dom";
import { useDispatch } from "hooks/redux";
import { logout } from "services";

export interface AuthProps {
  auth: boolean | null;
}

export const withAuth = () => (WrappedComponent: any) => {
  const Component = (props: any) => {
    const dispatch = useDispatch();
    const [auth, _auth] = useState<boolean | null>(null); // null for initial loading, true/false for authenticated status
    const location = useLocation();
    
    // Memoize the authentication check function to prevent unnecessary re-creations
    // `dispatch` is a stable reference from `useDispatch`, so it won't cause `checkAuthenticated` to change.
    const checkAuthenticated = useCallback(async () => {
      try {
        await Auth.currentSession(); // Checks if a valid session exists
        await Auth.currentAuthenticatedUser(); // Gets user info, might refresh tokens
        // If both succeed, the user is authenticated.
        // Only update state if it's currently not true, to avoid unnecessary re-renders.
        _auth(prevAuth => (prevAuth !== true ? true : prevAuth));
      } catch (err) {
        // If any of the above fail, the user is not authenticated.
        console.error("Authentication check failed:", err);
        dispatch(logout()); // Clear user data from Redux store
        _auth(false); // Set auth state to false, triggering redirect
      }
    }, [dispatch]); // `dispatch` is a stable reference
    
    useEffect(() => {
      // This effect will run on:
      // 1. Initial mount of the `withAuth` wrapper component.
      // 2. Every time the `location` object changes (i.e., on every navigation).
      //
      // Crucially, we no longer call `_auth(null)` here.
      // The `auth` state will only change if `checkAuthenticated()` explicitly
      // sets it to `true` or `false`.
      checkAuthenticated();
    }, [location, checkAuthenticated]); // `checkAuthenticated` is stable due to `useCallback`
    
    return <WrappedComponent auth={auth} {...props} />;
  };
  
  return Component;
};
